import * as React from 'react';
import { useFormikContext } from 'formik'
import useAutocomplete from '@mui/material/useAutocomplete';
import CheckIcon from '@mui/icons-material/Check';
import styles from './form-autocomplete.module.scss';
import {AutoCompleteItemValue} from './types';
import { Tag } from '../../tag';

interface AutoCompleteProps extends React.InputHTMLAttributes<HTMLSelectElement> {
    placeHolder: string;
    label: string;
    name: string;
    items: AutoCompleteItemValue[];
    selectedItems?: AutoCompleteItemValue[];
    hint?: string;
    onFocus?: () => void;
}

const FormAutocomplete = (props:AutoCompleteProps) => {
    const {name, placeHolder, label, items, selectedItems, hint, onFocus} = props;

    const resolvedItems = React.useMemo(() => {
        const combined = [...items];

        (selectedItems ?? []).forEach(item => {
            if (!combined.some(existing => existing.id === item.id)) {
                combined.push(item);
            }
        });

        return combined;
    }, [items, selectedItems]);

    const defaultValues:AutoCompleteItemValue[] = React.useMemo(() => (
        (selectedItems ?? [])
            .map(item => resolvedItems.find(option => option.id == item.id))
            .filter((item): item is AutoCompleteItemValue => Boolean(item))
    ), [resolvedItems, selectedItems]);

    const {setFieldValue} = useFormikContext();
    const {
        getRootProps,
        getInputLabelProps,
        getInputProps,
        getTagProps,
        getListboxProps,
        getOptionProps,
        groupedOptions,
        value,
        setAnchorEl,
        //focused
    } = useAutocomplete({
        id: 'autocomplete-tag',
        defaultValue: defaultValues,
        multiple: true,
        options: resolvedItems,
        getOptionLabel: (option) => option.name,
        filterSelectedOptions: true,
        isOptionEqualToValue: (option, value) => option && value && option.id === value.id,
        onChange: (e, value) => {
            setFieldValue(name, value, false)
        },
        openOnFocus: false
    });

    const selectedValues = React.useMemo(
        () => (value ?? []).filter((item): item is AutoCompleteItemValue => Boolean(item)),
        [value]
    );
    const hasSelectedValues = selectedValues.length > 0;
    const wrapperClassName = [
        styles.inputWrapper,
        hasSelectedValues ? styles.inputWrapperDense : ''
    ].filter(Boolean).join(' ');
    const inputProps = getInputProps();

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        inputProps.onFocus?.(event);
        onFocus?.();
    };

    return (
        <div className={styles.root}>
            <div {...getRootProps()}>
                <label {...getInputLabelProps} style={{marginBottom:'8px'}} className={styles.autocompleteName}>{label}</label>
                <div className={styles.inputArea}>
                    <div ref={setAnchorEl} className={wrapperClassName}>
                        <div className={styles.tagsWrapper}>
                            {selectedValues.map((option: AutoCompleteItemValue, index: number) => {
                                const { key, ...tagProps } = getTagProps({index});
                                return <Tag label={option.name} key={key} {...tagProps} />
                            })}
                        </div>
                        <input
                            placeholder={placeHolder}
                            {...inputProps}
                            onFocus={handleFocus}
                        />
                    </div>
                    {groupedOptions.length > 0 ? (
                        <ul className={styles.listBox} {...getListboxProps()}>
                            {(groupedOptions as typeof resolvedItems).map((option, index) => {
                                const { key, ...optionProps } = getOptionProps({option, index});
                                return <li key={key} {...optionProps}>
                                    <span>{option.name}</span>
                                    <CheckIcon fontSize="small"/>
                                </li>
                            })}
                        </ul>
                    ) : null}
                </div>
                {hint && <div className={styles.hint}>{hint}</div>}
            </div>
        </div>
    );
}

export default FormAutocomplete;
